import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { FileText, Eye, Download, RefreshCw, Trash2, Loader2 } from 'lucide-react';

const openBrochure = async (courseId, file, download) => {
  const res = await api.get(`/courses/${courseId}/brochure-url`, { params: { file_key: file.file_key, download } });
  if (download) {
    const a = document.createElement('a');
    a.href = res.data.url;
    a.download = file.filename || 'brochure.pdf';
    a.click();
  } else {
    window.open(res.data.url, '_blank', 'noopener');
  }
};

const ActionButton = ({ icon: Icon, label, onClick, disabled, danger, testId }) => (
  <Button
    type="button"
    size="sm"
    variant="outline"
    onClick={onClick}
    disabled={disabled}
    title={label}
    className={`rounded-full h-8 px-3 ${danger ? 'text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700' : ''}`}
    style={danger ? undefined : { borderColor: '#163329', color: '#163329' }}
    data-testid={testId}
  >
    <Icon className="h-4 w-4 sm:mr-1.5" />
    <span className="hidden sm:inline">{label}</span>
  </Button>
);

export const BrochureFileList = ({ courseId, files, onReplace, onDelete, busyIndex = null }) => {
  const inputRef = useRef(null);
  const [replaceIndex, setReplaceIndex] = useState(null);
  const [opening, setOpening] = useState(null);

  const open = async (file, index, download) => {
    if (!file.file_key) return window.open(file.presigned_url || file.url, '_blank', 'noopener');
    setOpening(`${index}-${download}`);
    try {
      await openBrochure(courseId, file, download);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open brochure');
    } finally {
      setOpening(null);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && replaceIndex !== null) onReplace(replaceIndex, file);
    setReplaceIndex(null);
  };

  return (
    <div className="space-y-2">
      {onReplace && <input ref={inputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={handleFile} data-testid="replace-brochure-input" />}
      {files.map((file, index) => (
        <div key={file.file_key || index} className="p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center gap-3" style={{ borderColor: '#E3E1DB', backgroundColor: '#F5F5F0' }} data-testid={`brochure-file-${index}`}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FileText className="h-5 w-5 flex-shrink-0" style={{ color: '#D86B47' }} />
            <div className="min-w-0">
              <p className="font-medium text-sm truncate" style={{ color: '#163329' }}>{file.filename || `Brochure ${index + 1}.pdf`}</p>
              {file.size_bytes && <p className="text-xs" style={{ color: '#5C635A' }}>{(file.size_bytes / 1024 / 1024).toFixed(2)} MB</p>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {busyIndex === index ? (
              <span className="flex items-center gap-2 text-xs" style={{ color: '#5C635A' }}><Loader2 className="h-4 w-4 animate-spin" /> Replacing...</span>
            ) : (
              <>
                <ActionButton icon={opening === `${index}-false` ? Loader2 : Eye} label="View" onClick={() => open(file, index, false)} testId={`view-brochure-${index}`} />
                <ActionButton icon={opening === `${index}-true` ? Loader2 : Download} label="Download" onClick={() => open(file, index, true)} testId={`download-brochure-${index}`} />
                {onReplace && <ActionButton icon={RefreshCw} label="Replace" disabled={busyIndex !== null} onClick={() => { setReplaceIndex(index); inputRef.current?.click(); }} testId={`replace-brochure-${index}`} />}
                {onDelete && <ActionButton icon={Trash2} label="Delete" danger disabled={busyIndex !== null} onClick={() => onDelete(index)} testId={`remove-brochure-${index}`} />}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
